/**
 * @file CardBrandIcon.tsx
 * @brief Compact card-network glyphs (Visa / Mastercard / Amex / default).
 *
 * @details
 * Readable marks at picker size — wordmarks use SVG text (not broken path
 * approximations). Sized 32×20 to stay crisp in Select rows. (28-07-2026)
 *
 * @author DES
 * @date 28-07-2026
 */

import { cn } from "@/shared/theme/cn";
import { normalizeBrand } from "./cardBrand";

const SHELL =
  "block h-5 w-8 shrink-0 overflow-hidden rounded-[3px] [&>svg]:block [&>svg]:h-full [&>svg]:w-full";

function VisaMark() {
  return (
    <svg viewBox="0 0 32 20" xmlns="http://www.w3.org/2000/svg">
      <rect width="32" height="20" rx="3" fill="#1A1F71" />
      <text
        x="16"
        y="13.5"
        textAnchor="middle"
        fill="#fff"
        fontFamily="Arial, Helvetica, sans-serif"
        fontSize="8"
        fontStyle="italic"
        fontWeight="800"
        letterSpacing="0.6"
      >
        VISA
      </text>
    </svg>
  );
}

function MastercardMark() {
  return (
    <svg viewBox="0 0 32 20" xmlns="http://www.w3.org/2000/svg">
      <rect width="32" height="20" rx="3" fill="#000" />
      <circle cx="12.5" cy="10" r="5.5" fill="#EB001B" />
      <circle cx="19.5" cy="10" r="5.5" fill="#F79E1B" />
      <path
        d="M16 5.2a5.5 5.5 0 0 1 0 9.6 5.5 5.5 0 0 1 0-9.6z"
        fill="#FF5F00"
      />
    </svg>
  );
}

function AmexMark() {
  return (
    <svg viewBox="0 0 32 20" xmlns="http://www.w3.org/2000/svg">
      <rect width="32" height="20" rx="3" fill="#2E77BB" />
      <text
        x="16"
        y="13.5"
        textAnchor="middle"
        fill="#fff"
        fontFamily="Arial, Helvetica, sans-serif"
        fontSize="6.5"
        fontWeight="800"
        letterSpacing="0.4"
      >
        AMEX
      </text>
    </svg>
  );
}

function DefaultMark() {
  return (
    <svg viewBox="0 0 32 20" xmlns="http://www.w3.org/2000/svg">
      <rect width="32" height="20" rx="3" fill="#374151" />
      <rect x="0" y="5" width="32" height="3.5" fill="#1F2937" />
      <rect x="4" y="13" width="10" height="2.5" rx="1" fill="#9CA3AF" />
      <rect x="22" y="13" width="6" height="2.5" rx="1" fill="#6B7280" />
    </svg>
  );
}

/** Renders the network mark for a Stripe-style brand string. */
export function CardBrandIcon({
  brand,
  className,
}: {
  brand?: string | null;
  className?: string;
}) {
  const key = normalizeBrand(brand ?? "");
  let mark = <DefaultMark />;
  if (key === "visa") mark = <VisaMark />;
  else if (key === "mastercard") mark = <MastercardMark />;
  else if (key === "amex" || key === "americanexpress") mark = <AmexMark />;

  return <span className={cn(SHELL, className)}>{mark}</span>;
}
